import { Minus, Plus, X } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const clampZoom = (zoom: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(zoom.toFixed(2))));

type DragStart = {
  x: number;
  y: number;
  scrollLeft: number;
  scrollTop: number;
};

interface ImagePreviewModalProps {
  isOpen: boolean;
  src: string;
  alt: string;
  onClose: () => void;
}

export function ImagePreviewModal({
  isOpen,
  src,
  alt,
  onClose,
}: ImagePreviewModalProps) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [isDragging, setIsDragging] = useState(false);
  const zoomContainerRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<DragStart | null>(null);

  const resetInteraction = () => {
    setZoom(MIN_ZOOM);
    setIsDragging(false);
    dragStartRef.current = null;
  };

  const close = () => {
    resetInteraction();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    resetInteraction();
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        setZoom((currentZoom) => clampZoom(currentZoom + ZOOM_STEP));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        setZoom((currentZoom) => clampZoom(currentZoom - ZOOM_STEP));
      } else if (event.key === "0") {
        event.preventDefault();
        setZoom(MIN_ZOOM);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, src]);

  useEffect(() => {
    if (!isOpen || zoom <= MIN_ZOOM) {
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }, [isOpen, zoom]);

  if (!isOpen) return null;

  const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (zoom <= MIN_ZOOM || event.button !== 0) return;

    const container = zoomContainerRef.current;
    if (!container) return;

    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    setIsDragging(true);
    event.preventDefault();
  };

  const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const container = zoomContainerRef.current;
    const dragStart = dragStartRef.current;
    if (!isDragging || !container || !dragStart) return;

    container.scrollLeft = dragStart.scrollLeft - (event.clientX - dragStart.x);
    container.scrollTop = dragStart.scrollTop - (event.clientY - dragStart.y);
  };

  const stopDrag = () => {
    setIsDragging(false);
    dragStartRef.current = null;
  };

  const isMinZoom = zoom <= MIN_ZOOM;
  const isMaxZoom = zoom >= MAX_ZOOM;
  const cursorClass =
    zoom > MIN_ZOOM
      ? isDragging
        ? "cursor-grabbing"
        : "cursor-grab"
      : "cursor-default";

  return (
    <div
      className="modal-overlay !bg-slate-950/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Visualizacao da imagem em tela cheia"
      onClick={close}
    >
      <div
        className="relative flex max-h-full max-w-full items-center justify-center"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-1 rounded-lg bg-slate-900/75 p-1 shadow-lg sm:top-4">
          <button
            type="button"
            aria-label="Afastar imagem"
            onClick={() => setZoom((current) => clampZoom(current - ZOOM_STEP))}
            disabled={isMinZoom}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white transition hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Minus size={16} />
          </button>
          <span
            className="w-12 text-center text-xs font-semibold text-white"
            aria-live="polite"
          >
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            aria-label="Aproximar imagem"
            onClick={() => setZoom((current) => clampZoom(current + ZOOM_STEP))}
            disabled={isMaxZoom}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white transition hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
          </button>
          <button
            type="button"
            aria-label="Redefinir zoom da imagem"
            onClick={() => setZoom(MIN_ZOOM)}
            className="rounded-md px-2 py-1 text-xs font-semibold text-white transition hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          >
            100%
          </button>
        </div>

        <button
          type="button"
          aria-label="Fechar imagem em tela cheia"
          onClick={close}
          className="btn-icon absolute right-2 top-2 z-10 h-9 w-9 bg-slate-900/75 text-white hover:bg-slate-900 focus-visible:ring-slate-300"
        >
          <X size={18} />
        </button>

        <div
          ref={zoomContainerRef}
          className={`h-[88vh] w-[92vw] overflow-auto rounded-lg ${cursorClass}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            className="rounded-lg object-contain shadow-2xl select-none"
            style={{
              width: `${zoom * 100}%`,
              height: `${zoom * 100}%`,
              maxWidth: "none",
              maxHeight: "none",
              minWidth: "100%",
              minHeight: "100%",
            }}
          />
        </div>
      </div>
    </div>
  );
}
